import {
  CAPABILITY_IDS,
  capabilityOf,
  meetsPlan,
  PLAN_IDS,
  PLANS,
  RESERVAS_PER_BOOKING_CENTS,
  type CapabilityId,
  type PlanId,
} from '@dale/plans';

import { formatMoneyCents } from '../lib/format';
import { Dollars } from './Figures';

/** Comandas and the Menú exist for restaurants only; nobody else needs a row for them. */
const RESTAURANT_ONLY: ReadonlySet<CapabilityId> = new Set(['comandas', 'menu']);
/** The logins already have their own row, with each plan's count. */
const COUNTED: ReadonlySet<CapabilityId> = new Set(['accesos']);

type PlanComparisonProps = {
  /** The plan the business is on: its column says so. */
  current: PlanId;
  /** The plan picked above: its column is lit. */
  selected: PlanId;
  restaurant: boolean;
  /** What the business would pay a month on each plan, as the picker above says it. */
  priceFor: (plan: PlanId) => number;
  onChoose: (plan: PlanId) => void;
};

/**
 * Every plan side by side, from the plan catalog (@dale/plans): what each one
 * includes, how many logins, and its price a month. A column header picks
 * that plan for the change below, the same choice as the picker above.
 */
export function PlanComparison({ current, selected, restaurant, priceFor, onChoose }: PlanComparisonProps) {
  const rows = CAPABILITY_IDS.filter((id) => !COUNTED.has(id) && (restaurant || !RESTAURANT_ONLY.has(id)));
  return (
    <div className="plan-compare-scroll">
      <table className="plan-compare">
        <caption className="sr-only">Qué incluye cada plan</caption>
        <thead>
          <tr>
            <td className="plan-compare-corner" />
            {PLAN_IDS.map((plan) => (
              <th key={plan} scope="col" data-selected={plan === selected || undefined} data-current={plan === current || undefined}>
                <span className="plan-compare-name">{PLANS[plan].name}</span>
                {plan === current ? <span className="plan-compare-current">Tu plan</span> : null}
                {plan === 'reservas' ? (
                  // Independientes has no monthly fee: it pays per booking.
                  <span className="plan-compare-per-booking">{formatMoneyCents(RESERVAS_PER_BOOKING_CENTS)} por reserva</span>
                ) : (
                  <span className="plan-compare-price">
                    <Dollars cents={priceFor(plan)} />
                    <small> al mes</small>
                  </span>
                )}
                {plan === current ? null : (
                  <button
                    type="button"
                    className="plan-compare-pick"
                    aria-label={`Elegir ${PLANS[plan].name}`}
                    aria-pressed={plan === selected}
                    onClick={() => onChoose(plan)}
                  >
                    Elegir
                  </button>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {PLANS.reservas.adds.slice(0, 2).map((line) => (
            <tr key={line}>
              <th scope="row">{line}</th>
              {PLAN_IDS.map((plan) => (
                <Included key={plan} yes selected={plan === selected} />
              ))}
            </tr>
          ))}
          <tr>
            <th scope="row">Accesos</th>
            {PLAN_IDS.map((plan) => (
              <td key={plan} data-selected={plan === selected || undefined} className="plan-compare-seats">
                {PLANS[plan].seats}
              </td>
            ))}
          </tr>
          {rows.map((id) => (
            <tr key={id}>
              <th scope="row">{capabilityOf(id).title}</th>
              {PLAN_IDS.map((plan) => (
                <Included key={plan} yes={meetsPlan(plan, capabilityOf(id).minPlan)} selected={plan === selected} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A check or a quiet dash, with the word for screen readers. */
function Included({ yes, selected }: { yes: boolean; selected: boolean }) {
  return (
    <td data-selected={selected || undefined} data-yes={yes || undefined}>
      {yes ? (
        <svg className="plan-compare-check" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
          <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <span className="plan-compare-dash" aria-hidden="true" />
      )}
      <span className="sr-only">{yes ? 'Sí' : 'No'}</span>
    </td>
  );
}
